// Keep completed drawings selectable without leaving their edit grips visible.
// amCharts exposes drawing events publicly; grip visibility is isolated here
// because its default hover behaviour also shows endpoint handles.
export function configureDrawingInteraction(control, { calloutFill, contrastColor, lightTheme = false, onFinished = () => {} } = {}) {
  const chart = control.get('stockChart');
  const root = chart.root;
  const seen = new WeakSet();
  const sprites = new WeakSet();
  const panels = new WeakSet();
  const manualDefaults = new WeakMap();
  const resizing = new WeakMap();
  const drawingContrast = (series, channel) => (color, target) => {
    const index = target?.dataItem?.dataContext?.index ?? series._getIndex(target);
    if (series.data.values.some(row => row.index === index && row['manualLight' + channel])) return color;
    return contrast(color);
  };
  const contrast = color => {
    if (!lightTheme || !color?.toCSSHex) return color;
    const hex = color.toCSSHex().replace('#', '');
    const rgb = [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16));
    return rgb.every(v => v > 210) ? contrastColor : color;
  };
  const disposers = [];
  disposers.push(root.events.once('frameended', () => {
    for (const [key, channel, apply] of [['strokeControl', 'Stroke', '_setStroke'], ['fillControl', 'Fill', '_setFill']]) {
      const picker = control.getPrivate(key);
      if (!picker) continue;
      disposers.push(picker.getPrivate('dropdown').events.on('invoked', () => {
        if (!lightTheme) return;
        chart.panels.each(panel => panel.series.each(series => {
          if (!series.isType('DrawingSeries')) return;
          if (series._selected.length) {
            for (const row of series.data.values) if (series._selected.includes(row.index)) row['manualLight' + channel] = true;
          } else if (series._drawingEnabled) {
            manualDefaults.set(series, {...manualDefaults.get(series), ['manualLight' + channel]: true});
          }
        }));
        // Reapply even when the user chooses the already stored colour.
        control[apply]();
      }));
    }
  }));
  const sync = series => {
    if (series.isDisposed()) return;
    if (lightTheme && contrastColor) {
      series.circles?.each(dot => {
        dot.setAll({fill: contrastColor, stroke: calloutFill, strokeWidth: 2, fillOpacity: 1, strokeOpacity: 1});
        if (!sprites.has(dot)) {
          sprites.add(dot);
          dot.adapters.add('fill', () => contrastColor);
          dot.adapters.add('stroke', () => calloutFill);
        }
      });
    }
    if (series.constructor.className === 'AverageSeries') {
      series.set('showExtension', false);
      for (const row of series.data.values) row.showExtension = false;
      series.lines?.each(line => line.set('forceHidden', true));
    }
    if (series.constructor.className === 'CalloutSeries') {
      for (const item of series.dataItems) {
        const row = item.dataContext;
        const bg = row?.background;
        if (bg && !sprites.has(bg)) {
          sprites.add(bg);
          const fill = row.bgSettings?.get('fill') ?? calloutFill;
          const sameAsText = fill?.toCSSHex?.() === row.settings?.get('fill')?.toCSSHex?.();
          const style = {
            fill: sameAsText ? calloutFill : fill,
            fillOpacity: sameAsText ? 0.95 : (row.bgSettings?.get('fillOpacity') ?? 0.95),
          };
          // Every background needs persistent states, including black/custom fills.
          bg.setAll(style);
          bg.states.lookup('default').setAll(style);
          bg.states.lookup('active')?.setAll(style);
          row.bgSettings?.setAll(style);
        }
      }
    }
    for (const item of series.dataItems) {
      for (const sprite of [item.dataContext?.sprite, item.dataContext?.label]) {
        if (!sprite || sprites.has(sprite) || sprite.isDisposed()) continue;
        sprites.add(sprite);
        sprite.set('cursorOverStyle', 'pointer');
        if (sprite === item.dataContext?.sprite) sprite.adapters.add('stroke', drawingContrast(series, 'Stroke'));
        if (series.constructor.className === 'IconSeries') sprite.adapters.add('fill', drawingContrast(series, 'Fill'));
        if (sprite === item.dataContext?.label) {
          sprite.addDisposer(sprite.on('active', active => {
            if (active) return;
            series.addDisposer(root.events.once('frameended', () => {
              if (series.isDisposed() || sprite.get('active')) return;
              series._isEditing = false;
              control.setRaw('tool', undefined);
              control._setTool(undefined);
              chart.set('drawingSelectionEnabled', true);
              chart.setPrivate('drawingSelectionEnabled', true);
              chart.unselectDrawings();
              series._hideResizer();
              onFinished();
            }));
          }));
        }
        sprite.addDisposer(sprite.events.on('pointerover', () => chart.setPrivate('drawingHovered', true)));
        sprite.addDisposer(sprite.events.on('pointerout', () => chart.setPrivate('drawingHovered', false)));
      }
    }
    // Label/Callout grips contain the actual text, not endpoint handles.
    if (['LabelSeries', 'CalloutSeries'].includes(series.constructor.className)) return;
    series.grips?.each(grip => {
      const index = grip.dataItem?.dataContext?.index;
      const editing = series._isDrawing || series._selected.includes(index) || resizing.get(series) === index;
      grip.set('forceHidden', !editing);
      if (editing) grip.show(0);
    });
  };
  const bindSeries = series => {
    if (!series?.isType('DrawingSeries') || seen.has(series)) return;
    seen.add(series);
    if (series.constructor.className === 'DoodleSeries') {
      // An invisible fill still participates in hit testing. Doodles are open paths.
      series.strokes.template.set('fill', undefined);
      series.strokes.template.adapters.add('fill', () => undefined);
    }
    if (series.constructor.className === 'AverageSeries') {
      const updateLine = series._updateLine;
      series._updateLine = function (index, _start, _end, p1, p2) {
        this._lines[index]?.set('segments', []);
        this._hitLines[index]?.set('segments', [[[p1, p2]]]);
      };
      series.addDisposer({dispose() { series._updateLine = updateLine; }});
    }
    if (series.constructor.className === 'FibonacciTimezoneSeries') {
      // Fibonacci draws its visible vertical levels with strokes, while the
      // SimpleLine base disables their interaction in favour of a diagonal hit line.
      series.strokes.template.setAll({forceInactive: false, interactive: true, draggable: true});
      const enableSelection = series.enableDrawingSelection;
      series.enableDrawingSelection = function (value) {
        enableSelection.call(this, value);
        this.strokes.template.set('forceInactive', !value);
      };
      series.addDisposer({dispose() { series.enableDrawingSelection = enableSelection; }});
    }
    // Hide selection furniture only for whole-drawing moves. Endpoint resizing
    // uses the separate bullet-drag lifecycle and keeps its handles visible.
    const moving = new Map();
    const moveAdapters = new WeakSet();
    const moveStart = series._handleFillDragStart;
    const moveStop = series._handleFillDragStop;
    const restoreMove = index => {
      const saved = moving.get(index);
      if (!saved) return;
      moving.delete(index);
      saved.forEach(({sprite, opacity}) => {
        if (!sprite.isDisposed()) sprite.set('opacity', opacity);
      });
    };
    series._handleFillDragStart = function (event, index) {
      restoreMove(index);
      moveStart.call(this, event, index);
      const text = ['LabelSeries', 'CalloutSeries'].includes(this.constructor.className);
      const parts = [
        ...(!text ? this.grips.values.filter(grip => grip.dataItem?.dataContext?.index === index) : []),
        ...this.selectors.values.filter(selector => selector.get('userData') === index),
      ];
      // Text grips contain the label itself; its resizer is hidden by the library.
      moving.set(index, parts.map(sprite => ({sprite, opacity: sprite.get('opacity', 1)})));
      parts.forEach(sprite => {
        if (!moveAdapters.has(sprite)) {
          moveAdapters.add(sprite);
          sprite.adapters.add('opacity', value => moving.has(index) ? 0 : value);
        }
        sprite.set('opacity', 0);
      });
    };
    series._handleFillDragStop = function (event, index) {
      try { moveStop.call(this, event, index); }
      finally { restoreMove(index); }
    };
    series.addDisposer({dispose() {
      [...moving.keys()].forEach(restoreMove);
      series._handleFillDragStart = moveStart;
      series._handleFillDragStop = moveStop;
    }});
    const dragStart = series._handleBulletDragStart;
    series._handleBulletDragStart = function (event) {
      const index = event.target.dataItem?.dataContext?.index;
      if (index != null) resizing.set(this, index);
      dragStart.call(this, event);
      sync(this);
    };
    series.addDisposer({dispose() { resizing.delete(series); series._handleBulletDragStart = dragStart; }});
    const dragStop = series._handleBulletDragStop;
    series._handleBulletDragStop = function (event) {
      dragStop.call(this, event);
      resizing.delete(this);
      const index = event.target.dataItem?.dataContext?.index;
      if (index == null || this._isDrawing) return;
      this.addDisposer(root.events.once('frameended', () => {
        if (!this.isDisposed() && !this._selected.includes(index)) this._selectDrawing(index, false, true);
      }));
    };
    series.addDisposer({dispose() { series._handleBulletDragStop = dragStop; }});
    const selectDrawing = series._selectDrawing;
    series._selectDrawing = function (index, keepSelection, force) {
      if (this.constructor.className === 'AverageSeries' && this._isDrawing) return;
      if (this._isDrawing) return;
      // A pointer click emitted at the end of a handle drag must not toggle
      // the drawing off. It is still the user's active selection.
      if (this._isDragging && this._selected.includes(index) && !keepSelection) return;
      return selectDrawing.call(this, index, keepSelection, force);
    };
    series.addDisposer({dispose() { series._selectDrawing = selectDrawing; }});

    for (const key of ['strokes', 'fills', 'lines', 'hitLines', 'ellipses', 'arrows', 'labels']) {
      const template = series[key]?.template;
      if (template) {
        template.set('cursorOverStyle', 'pointer');
        template.adapters.add('stroke', drawingContrast(series, 'Stroke'));
        if (['fills', 'ellipses', 'arrows'].includes(key)) template.adapters.add('fill', drawingContrast(series, 'Fill'));
        series.addDisposer(template.events.on('pointerover', () => chart.setPrivate('drawingHovered', true)));
        series.addDisposer(template.events.on('pointerout', () => chart.setPrivate('drawingHovered', false)));
      }
    }
    if (!['LabelSeries', 'CalloutSeries'].includes(series.constructor.className)) series.grips?.template.set('forceHidden', true);
    if (series.constructor.className === 'CalloutSeries') {
      const tweak = series._tweakBullet;
      series._tweakBullet = function (container, item) {
        tweak.call(this, container, item);
        const row = item.dataContext;
        const label = row.label;
        if (!label) return;
        // The library treats any second click as edit, even minutes later.
        // A single click always selects; only an actual double-click edits.
        label.events.off('click');
        label.events.on('click', event => {
          if (label.get('active')) return;
          this._isEditing = false;
          this._isSelected = true;
          if (!this._selected.includes(row.index)) this._selectDrawing(row.index, !!event.originalEvent?.ctrlKey, true);
          else this._dispatchStockEvent('drawingselected', this.indexToDrawingId(row.index), row.index);
          this.spriteResizer.set('sprite', label);
        });
        label.events.on('dblclick', event => {
          this._isEditing = true;
          if (!this._selected.includes(row.index)) this._selectDrawing(row.index, !!event.originalEvent?.ctrlKey, true);
          else this._dispatchStockEvent('drawingselected', this.indexToDrawingId(row.index), row.index);
          this.spriteResizer.set('sprite', undefined);
          label.set('active', true);
        });
      };
      series.addDisposer({dispose() { series._tweakBullet = tweak; }});
      const apply = series._applySettings;
      series._applySettings = function (index, settings) {
        apply.call(this, index, settings);
        const row = this._getContext(index);
        if (!row?.background || !settings) return;
        // EditableLabel restores these states when text editing ends.
        // Keep them in sync with the user's chosen background.
        const style = {};
        for (const key of ['fill', 'fillOpacity', 'stroke', 'strokeOpacity']) {
          if (settings[key] !== undefined) style[key] = settings[key];
        }
        row.background.states.lookup('default')?.setAll(style);
        row.background.states.lookup('active')?.setAll(style);
      };
      series.addDisposer({dispose() { series._applySettings = apply; }});
    }
    const show = series._showSegmentBullets;
    series._showSegmentBullets = function (index) {
      if (this._isDrawing || this._selected.includes(index) || resizing.get(this) === index) show.call(this, index);
      sync(this);
    };
    series.addDisposer({dispose() { series._showSegmentBullets = show; }});
    series.addDisposer(series.events.on('datavalidated', () => {
      sync(series);
      if (!control.get('active')) chart.setPrivate('drawingSelectionEnabled', true);
    }));
    if (series.grips) series.addDisposer(series.grips.events.onAll(() => sync(series)));
    sync(series);
  };
  const bindPanel = panel => {
    if (panels.has(panel)) return;
    panels.add(panel);
    panel.series.each(bindSeries);
    const background = panel.plotContainer.get('background');
    if (background) panel.addDisposer(background.events.on('click', () => {
      if (!chart.getPrivate('drawingHovered') && !panel.series.values.some(series => series.isType('DrawingSeries') && (series._drawingEnabled || series._isEditing || series._isDragging))) {
        chart.unselectDrawings();
        panel.series.each(series => {
          if (series.isType('DrawingSeries')) series._hideResizer();
        });
      }
    }));
    panel.addDisposer(panel.series.events.onAll(event => { if (event.newValue) bindSeries(event.newValue); }));
  };
  disposers.push(control.on('active', active => {
    if (!active) {
      const pending = root.events.once('frameended', () => {
        if (!chart.isDisposed()) {
          chart.setPrivate('drawingSelectionEnabled', true);
          chart.panels.each(panel => panel.drawings.each(series => {
            // Text tools place their clickable label inside the grip container.
            // Closing the toolbar must not make existing labels unclickable.
            if (['LabelSeries', 'CalloutSeries'].includes(series.constructor.className)) {
              series.grips?.each(grip => grip.set('forceInactive', false));
            }
          }));
        }
      });
      disposers.push(pending);
    }
  }));
  chart.panels.each(bindPanel);
  disposers.push(chart.panels.events.onAll(event => { if (event.newValue) bindPanel(event.newValue); }));
  for (const event of ['drawingselected', 'drawingunselected']) {
    disposers.push(chart.events.on(event, e => {
      sync(e.series);
      if (event === 'drawingselected') {
        // DrawingControl selects the matching tool to populate style controls.
        // Keep those controls, but do not let selecting a shape start a new one.
        e.series.addDisposer(root.events.once('frameended', () => {
          if (chart.isDisposed() || e.series.isDisposed()) return;
          const selectedSprite = chart.spriteResizer?.get('sprite');
          control._currentEnabledSeries.forEach(series => series.disableDrawing());
          // All text series share one resizer. Restore it after stopping ALL tools.
          if (selectedSprite && e.series._selected.length && !selectedSprite.get('active')) {
            chart.spriteResizer.set('sprite', selectedSprite);
          }
          control._currentEnabledSeries = [];
          control.setRaw('tool', undefined);
        }));
      }
    }));
  }
  disposers.push(chart.events.on('drawingadded', e => {
    const manual = manualDefaults.get(e.series);
    if (manual) for (const row of e.series.data.values) if (row.index === e.index) Object.assign(row, manual);
    sync(e.series);
    // Text remains editable until the user finishes typing. Multi-point tools
    // must finish their last point before switching back to selection.
    if (e.series._isDrawing || ['LabelSeries', 'CalloutSeries'].includes(e.series.constructor.className)) return;
    const pending = root.events.once('frameended', () => {
      if (chart.isDisposed() || e.series.isDisposed()) return;
      control.setRaw('tool', undefined);
      // Finish the tool transition before restoring selection: _setTool clears it.
      control._setTool(undefined);
      chart.set('drawingSelectionEnabled', true);
      chart.setPrivate('drawingSelectionEnabled', true);
      chart.unselectDrawings();
      onFinished();
      sync(e.series);
    });
    e.series.addDisposer(pending);
  }));
  disposers.push(chart.events.on('drawingremoved', e => {
    e.series._isEditing = false;
    e.series._isSelected = false;
    e.series._isHover = false;
    e.series._hideResizer();
    chart.setPrivate('drawingHovered', false);
    control.setRaw('tool', undefined);
  }));
  root.addDisposer({dispose() { disposers.forEach(d => d.dispose()); }});
}


export function configureDrawingActions(control, toolbar) {
  const chart = control.get('stockChart');
  const root = chart.root;
  const allSeries = () => chart.panels.values.flatMap(p => p.series.values.filter(s => s.isType('DrawingSeries')));
  const clear = control.getPrivate('clearControl')?.getPrivate('button');
  const erase = control.getPrivate('eraserControl')?.getPrivate('button');
  const tool = control.getPrivate('toolControl');
  if (!clear || !erase || !tool) return;
  for (const key of ['boldControl', 'italicControl', 'fillControl', 'labelFillControl', 'strokeControl']) {
    control.getPrivate(key)?.getPrivate('button')?.setAttribute('data-drawing-style', key);
  }
  toolbar.get('container').appendChild(clear);
  control.getPrivate('clearControl').setAll({name: '', description: 'Delete all drawings'});
  control.getPrivate('clearControl').getPrivate('label').style.display = 'none';
  clear.setAttribute('data-drawing-delete-all', 'true');
  clear.setAttribute('aria-label', 'Delete all drawings');
  control.getPrivate('eraserControl').set('description', 'Delete selected drawing');
  erase.setAttribute('data-drawing-delete-selected', 'true');
  erase.setAttribute('aria-label', 'Delete selected drawing');
  const visibility = () => { clear.style.setProperty('display', control.get('active') ? 'inline-flex' : 'none', 'important'); };
  visibility();
  const active = control.on('active', visibility);
  const selected = tool.events.on('selected', () => {
    allSeries().forEach(series => {
      series._canceled = false;
      series._isEditing = false; series._isSelected = false; series._isHover = false;
      series.unselectDrawings(); series._hideResizer();
    });
  });
  const eraseSelected = event => {
    event.preventDefault(); event.stopImmediatePropagation();
    allSeries().forEach(series => {
      for (const index of [...series._selected]) series._disposeIndex(index);
      series._isEditing = false; series._isSelected = false; series._isHover = false;
      series._hideResizer(); series.disableDrawing();
    });
    control._currentEnabledSeries = [];
    control.setRaw('tool', undefined);
    chart.set('erasingEnabled', false);
    chart.setPrivate('drawingSelectionEnabled', true);
  };
  erase.addEventListener('click', eraseSelected, true);
  root.addDisposer({dispose() { active.dispose(); selected.dispose(); erase.removeEventListener('click', eraseSelected, true); clear.remove(); }});
}
